import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Role Preview (admin-only): UI-level role override. The real admin
  // token/session is untouched — only the role the UI renders changes.
  const [previewRole, setPreviewRole] = useState(localStorage.getItem('role_preview'));

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Auto-sync the browser timezone so emails/SMS/notifications show times in
  // this user's local zone (fire-and-forget, memoized per user+zone).
  const syncTimezone = useCallback((accessToken, userId) => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!tz || !accessToken || !userId) return;
      if (localStorage.getItem('tz_synced') === `${userId}:${tz}`) return;
      axios.post(`${API}/auth/timezone`, { timezone: tz }, {
        headers: { Authorization: `Bearer ${accessToken}` }
      }).then(() => localStorage.setItem('tz_synced', `${userId}:${tz}`)).catch(() => {});
    } catch { /* timezone sync is best-effort */ }
  }, [API]);

  useEffect(() => {
    const initAuth = async () => {
      // CRITICAL: If returning from OAuth callback, skip the /me check.
      // AuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const { data: userData } = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(userData);
          setToken(savedToken);
          syncTimezone(savedToken, userData.id);
        } catch (err) {
          console.error('Auth init error:', err);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [API]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('token', data.access_token);
      // Fresh login must wipe any stale impersonation state left over
      // from an earlier session (browser closed mid-impersonation, tab
      // restored days later, etc). Without this, the new admin session
      // silently carries `admin_token_backup` + `impersonation_meta`
      // from the previous user — which makes the next "Sign in as"
      // click read the wrong token and 403.
      localStorage.removeItem('impersonation_meta');
      localStorage.removeItem('admin_token_backup');
      localStorage.removeItem('role_preview');
      setPreviewRole(null);
      try { sessionStorage.removeItem('helper_tour_promo_shown'); } catch {}
      // Demo / test accounts: wipe every client-side "already seen" flag so
      // these emails always boot into the first-time experience (Voice Tour
      // promo, onboarding guides, fresh voice state). Also fire a backend
      // demo-reset to wipe role-specific data (e.g. radio talent invites)
      // so the Voice Tour mocks render on a truly empty account.
      const DEMO_EMAILS = new Set(['mike@avenuear.com', 'radiotest@test.com']);
      const normalizedEmail = email?.toLowerCase().trim();
      if (DEMO_EMAILS.has(normalizedEmail)) {
        try {
          Object.keys(localStorage).forEach((k) => {
            if (
              k.startsWith('onboarding_completed_') ||
              k === 'helper_explain_mode' ||
              k === 'helper_voice_on'
            ) {
              localStorage.removeItem(k);
            }
          });
          Object.keys(sessionStorage).forEach((k) => {
            if (k.startsWith('helper_') || k.startsWith('onboarding_')) {
              sessionStorage.removeItem(k);
            }
          });
        } catch {}
        // Backend reset for radio station demo account: clear talent invites
        // so the Invite History list is empty and the Voice Tour mocks render.
        if (normalizedEmail === 'radiotest@test.com') {
          try {
            await axios.post(
              `${API}/radio-station/demo-reset`,
              {},
              { headers: { Authorization: `Bearer ${data.access_token}` } }
            );
          } catch {}
        }
      }
      setToken(data.access_token);
      setUser(data.user);
      syncTimezone(data.access_token, data.user?.id);
      return data.user;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Login failed');
    }
  };

  // Direct auth setter for cases like accepting invites where we already have token/user
  const setAuth = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
    syncTimezone(accessToken, userData?.id);
  };

  const signup = async (email, password, role, displayName = '', phoneNumber = '', extraFields = {}) => {
    try {
      const { data } = await axios.post(`${API}/auth/signup`, { 
        email, 
        password, 
        role,
        display_name: displayName,
        phone_number: phoneNumber,
        phone_verification_token: extraFields.phone_verification_token || '',
        ...extraFields  // Include company_name, position, linkedin_url, phone for executives
      });
      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      syncTimezone(data.access_token, data.user?.id);
      // Return full response including message for pending approval
      return { user: data.user, message: data.message };
    } catch (err) {
      // Signup validation errors return `{ detail: { field, message } }`
      // so the frontend can highlight the exact input. Preserve both
      // pieces on the thrown Error for the caller.
      const detail = err.response?.data?.detail;
      let msg = 'Signup failed';
      let field = null;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (detail && typeof detail === 'object') {
        msg = detail.message || detail.detail || 'Signup failed';
        field = detail.field || null;
      }
      const wrapped = new Error(msg);
      wrapped.field = field;
      throw wrapped;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role_preview');
    setPreviewRole(null);
    // If we were mid-impersonation, clear those keys too so we don't
    // resurrect a fake session on next mount.
    localStorage.removeItem('impersonation_meta');
    localStorage.removeItem('admin_token_backup');
    try { sessionStorage.removeItem('helper_tour_promo_shown'); } catch {}
    setToken(null);
    setUser(null);
  };

  /**
   * Start an impersonation session — swap the current admin token
   * OUT to `admin_token_backup` and swap the new impersonation token
   * IN as the live `token`. The banner reads from `impersonation_meta`.
   * Never call from a non-admin session — the backend rejects it
   * anyway with 403.
   */
  const startImpersonation = async (userId, reason = null) => {
    // Bug fix 2026-08-04: when the admin is already mid-impersonation
    // (e.g. the last session never exited cleanly and the tab was
    // reopened, or they navigated back to /admin somehow), the raw
    // `localStorage.token` is the impersonation token — its role is
    // NOT admin, so the backend rejects the start call with:
    //   "Access denied. Required roles: admin"
    //
    // The TRUE admin token in that scenario lives in
    // `admin_token_backup`. Prefer it if present. This also keeps the
    // backup pointing at the real admin token across chained
    // impersonations (impersonating User A, then User B without
    // exiting) — critical for Exit to correctly restore admin state.
    const backupAdminToken = localStorage.getItem('admin_token_backup');
    const activeToken = localStorage.getItem('token');
    const adminTokenForRequest = backupAdminToken || activeToken;
    try {
      const { data } = await axios.post(
        `${API}/admin/impersonate/start`,
        { user_id: userId, reason },
        { headers: { Authorization: `Bearer ${adminTokenForRequest}` } },
      );
      // 1) Preserve the admin token so Exit can restore it. Only write
      //    the backup if it isn't already set — that way chained
      //    impersonation preserves the ORIGINAL admin token, not the
      //    previous impersonation token.
      if (!backupAdminToken) {
        localStorage.setItem('admin_token_backup', activeToken);
      }
      // 2) Meta powers the red banner + Exit button on every page.
      localStorage.setItem('impersonation_meta', JSON.stringify({
        session_id: data.session_id,
        target_email: data.target.email,
        target_role: data.target.role,
        target_user_id: data.target.user_id,
        expires_at: data.expires_at,
      }));
      // 3) Swap the live token — this triggers RequireAuth to pull
      //    the target user's row and re-render.
      localStorage.setItem('token', data.token);
      setToken(data.token);
      // 4) Re-fetch /auth/me so `user` reflects the target user.
      try {
        const me = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${data.token}` },
        });
        setUser(me.data);
      } catch { /* non-fatal — RequireAuth will pull it */ }
      return data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Give a much more actionable message than the raw
      // "Access denied. Required roles: admin" — 99% of the time
      // this now means the browser had a stale impersonation session
      // that we couldn't recover from.
      if (err.response?.status === 403) {
        // Clear any stale impersonation state so the next login
        // starts clean.
        localStorage.removeItem('impersonation_meta');
        localStorage.removeItem('admin_token_backup');
        throw new Error(
          "Your admin session couldn't be restored (likely a stale impersonation from an earlier tab). " +
          "Log out and log back in as admin, then try again.",
        );
      }
      throw new Error(detail || 'Impersonation failed');
    }
  };

  /**
   * Exit the current impersonation session — best-effort audit
   * ping with the impersonation token, then swap the admin token
   * back in. If the backup is gone (shouldn't happen), fall back to
   * a full logout so the admin at least isn't stuck as the artist.
   */
  const exitImpersonation = async () => {
    const impersonationToken = localStorage.getItem('token');
    // Fire-and-forget audit close.
    try {
      await axios.post(`${API}/admin/impersonate/end`, null, {
        headers: { Authorization: `Bearer ${impersonationToken}` },
      });
    } catch { /* non-fatal */ }
    const adminToken = localStorage.getItem('admin_token_backup');
    localStorage.removeItem('impersonation_meta');
    localStorage.removeItem('admin_token_backup');
    if (!adminToken) {
      logout();
      return;
    }
    localStorage.setItem('token', adminToken);
    setToken(adminToken);
    // Re-fetch admin identity.
    try {
      const me = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setUser(me.data);
    } catch { /* non-fatal */ }
  };

  /**
   * Re-fetch the current user from /auth/me. Used after Stripe checkout
   * returns so a freshly-paid artist's `has_selected_plan` flips to true
   * before any RequireAuth gate runs — fixes the post-payment redirect loop.
   */
  const refreshUser = useCallback(async () => {
    const savedToken = localStorage.getItem('token');
    if (!savedToken) return null;
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` },
      });
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, [API]);

  const startRolePreview = (role) => {
    if (user?.role !== 'admin') return;
    localStorage.setItem('role_preview', role);
    setPreviewRole(role);
  };

  const exitRolePreview = () => {
    localStorage.removeItem('role_preview');
    setPreviewRole(null);
  };

  // Only honor the preview flag for a REAL admin session — a leftover
  // flag on a non-admin account is ignored and the true role renders.
  const isRolePreview = !!previewRole && user?.role === 'admin';
  const effectiveUser = isRolePreview ? { ...user, role: previewRole } : user;

  const value = {
    user: effectiveUser,
    realUser: user,
    previewRole: isRolePreview ? previewRole : null,
    isRolePreview,
    startRolePreview,
    exitRolePreview,
    token,
    loading,
    login,
    signup,
    logout,
    setAuth,
    refreshUser,
    startImpersonation,
    exitImpersonation,
    isImpersonating: !!localStorage.getItem('impersonation_meta'),
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
